import { memo } from 'react'
import { Alert, Button, Box, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import PropTypes from 'prop-types'
import { formatApiErrorForDisplay, getApiErrorMessage } from '../../lib/apiErrors.js'

/**
 * ErrorState component - Reusable error message display with retry option
 * @param {string|Error|object} message - Error message string or thrown API error
 * @param {string} [detail] - Optional secondary line (overrides auto request id)
 * @param {object} sx - Additional sx styles
 * @param {function} onRetry - Optional retry callback function
 * @param {boolean} showRetry - Whether to show retry button (default: true if onRetry provided)
 */
export const ErrorState = memo(({ message, detail, sx, onRetry, showRetry = !!onRetry }) => {
  const isPlainString = typeof message === 'string'
  const { message: autoMessage, detail: autoDetail } = isPlainString
    ? { message, detail: detail ?? null }
    : formatApiErrorForDisplay(message)
  const displayMessage = isPlainString ? message : autoMessage || getApiErrorMessage(message)
  const displayDetail = detail ?? autoDetail

  return (
    <Alert
      severity="error"
      sx={{ m: 2, ...sx }}
      action={
        showRetry && onRetry ? (
          <Button
            color="inherit"
            size="small"
            onClick={onRetry}
            startIcon={<RefreshIcon />}
            aria-label="Retry"
          >
            Retry
          </Button>
        ) : null
      }
    >
      <Box>
        <Typography variant="body2" component="div">
          {displayMessage}
        </Typography>
        {displayDetail ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {displayDetail}
          </Typography>
        ) : null}
      </Box>
    </Alert>
  )
})

ErrorState.propTypes = {
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  detail: PropTypes.string,
  sx: PropTypes.object,
  onRetry: PropTypes.func,
  showRetry: PropTypes.bool,
}

ErrorState.displayName = 'ErrorState'
